import { useState } from 'react';
import LoadingMessages from '../components/LoadingMessages';
import { apiClient } from '../store/authStore';

const C = {
  bg:       '#0a0a0f',
  surface:  '#13131f',
  surfaceHi:'#181828',
  input:    '#1a1a2e',
  border:   'rgba(124,58,237,0.2)',
  borderHi: 'rgba(124,58,237,0.45)',
  violet:   '#7c3aed',
  blue:     '#3b82f6',
  text:     '#f1f5f9',
  muted:    '#94a3b8',
  faint:    '#475569',
  gradient: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  shadow:   '0 4px 24px rgba(124,58,237,0.1)',
  font:     "'Inter', system-ui, -apple-system, sans-serif",
};

const card = (extra = {}) => ({
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: '24px 26px',
  marginBottom: 16,
  boxShadow: C.shadow,
  ...extra,
});

const STATS_CONFIG = [
  { key: 'orphan_pages',    label: 'Orphan pages',    icon: '⚠', color: '#f87171', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.25)'   },
  { key: 'under_linked',    label: 'Under-linked',    icon: '⚡', color: '#fbbf24', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.25)'  },
  { key: 'suggestions',     label: 'Suggestions',     icon: '✦', color: '#a78bfa', bg: 'rgba(124,58,237,0.1)', border: 'rgba(124,58,237,0.25)'  },
  { key: 'pages_analyzed',  label: 'Pages analyzed',  icon: '◎', color: '#60a5fa', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)'  },
];

function StatCard({ cfg, value }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${cfg.border}`, borderRadius: 14, padding: '20px 22px', boxShadow: `0 4px 20px ${cfg.bg}`, transition: 'transform 0.15s, box-shadow 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 28px ${cfg.bg}`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 4px 20px ${cfg.bg}`; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: cfg.color }}>
          {cfg.icon}
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{cfg.label}</span>
      </div>
      <p style={{ fontSize: 36, fontWeight: 900, color: cfg.color, margin: 0, letterSpacing: '-1px', lineHeight: 1 }}>{value ?? '—'}</p>
    </div>
  );
}

function SectionCard({ title, count, icon, color, border, bg, children }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${border}`, borderRadius: 16, marginBottom: 16, overflow: 'hidden', boxShadow: `0 4px 20px ${bg}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: `1px solid ${border}`, background: bg }}>
        <span style={{ fontSize: 18, color }}>{icon}</span>
        <p style={{ fontSize: 14, fontWeight: 700, color, margin: 0 }}>{title}</p>
        {count !== undefined && (
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, background: `${color}20`, border: `1px solid ${color}40`, color, borderRadius: 20, padding: '2px 10px' }}>
            {count}
          </span>
        )}
      </div>
      <div style={{ padding: '20px 24px' }}>{children}</div>
    </div>
  );
}

export default function InternalLinks() {
  const [url, setUrl]         = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError]     = useState('');
  const [copied, setCopied]   = useState(null);

  const analyze = async () => {
    if (!url.trim()) return;
    setError(''); setLoading(true); setResults(null);
    try {
      const r1 = await apiClient.post('/traffic/audit/site', { url: url.trim(), max_pages: 200 });
      const jid = r1.job_id;

      // Poll until done
      let attempts = 0;
      const maxAttempts = 60;
      while (attempts < maxAttempts) {
        await new Promise(res => setTimeout(res, 4000));
        const st = await apiClient.get(`/traffic/audit/status/${jid}`);
        if (st.status === 'completed') {
          const data = await apiClient.get(`/traffic/audit/internal-links/${jid}`);
          setResults(data);
          break;
        } else if (st.status === 'failed') {
          setError(st.error || 'Analysis failed');
          break;
        }
        attempts++;
      }
    } catch (e) { setError(e.message || 'Failed to start analysis'); }
    finally { setLoading(false); }
  };

  const copyCode = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const orphans     = results?.orphan_pages || [];
  const underLinked = results?.under_linked || [];
  const suggestions = results?.link_suggestions || [];
  const stats       = results?.stats || {};

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: C.font, color: C.text }}>
      <main style={{ maxWidth: 960, margin: '0 auto', padding: '40px 28px 80px' }}>

        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#60a5fa' }}>⟶</div>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Internal Links</span>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: '0 0 6px', letterSpacing: '-0.8px' }}>Internal Link Optimizer</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Find orphan pages, under-linked content, and get smart linking suggestions to boost your SEO.</p>
        </div>

        {/* URL form */}
        <div style={card()}>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 4px' }}>Analyze your site</p>
          <p style={{ fontSize: 12, color: C.muted, margin: '0 0 16px' }}>Enter your website URL to map all internal link relationships.</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <input type="text" value={url} onChange={e => setUrl(e.target.value)}
              placeholder="https://yoursite.com"
              onKeyDown={e => e.key === 'Enter' && analyze()}
              disabled={loading}
              style={{ flex: 1, background: C.input, border: `1px solid rgba(59,130,246,0.3)`, borderRadius: 10, padding: '11px 14px', fontSize: 14, color: C.text, outline: 'none', fontFamily: C.font, transition: 'border-color 0.2s' }}
              onFocus={e => e.target.style.borderColor = '#3b82f6'}
              onBlur={e => e.target.style.borderColor = 'rgba(59,130,246,0.3)'}
            />
            <button onClick={analyze} disabled={loading || !url.trim()}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: loading ? C.faint : C.gradient, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', fontFamily: C.font, boxShadow: !loading ? '0 4px 16px rgba(124,58,237,0.3)' : 'none', transition: 'all 0.15s' }}>
              {loading ? (
                <>
                  <div style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%' }} className="animate-spin" />
                  Analyzing…
                </>
              ) : '⟶ Analyze links'}
            </button>
          </div>
          {loading && <LoadingMessages style={{ marginTop: 14 }} />}
          {error && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: 12, color: '#f87171' }}>
              {error}
            </div>
          )}
        </div>

        {/* Stats cards */}
        {results && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {STATS_CONFIG.map(cfg => (
                <StatCard key={cfg.key} cfg={cfg} value={
                  cfg.key === 'orphan_pages'   ? orphans.length :
                  cfg.key === 'under_linked'   ? underLinked.length :
                  cfg.key === 'suggestions'    ? suggestions.length :
                  cfg.key === 'pages_analyzed' ? (stats.total_pages || results.total_pages || '?') : '?'
                } />
              ))}
            </div>

            {/* Orphan pages */}
            {orphans.length > 0 && (
              <SectionCard
                title="Orphan pages" count={orphans.length}
                icon="⚠" color="#f87171" border="rgba(239,68,68,0.25)" bg="rgba(239,68,68,0.05)">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {orphans.map((page, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', background: C.surfaceHi, border: '1px solid rgba(239,68,68,0.15)', borderRadius: 10, padding: '12px 16px', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#f87171', margin: '0 0 3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.url || page}</p>
                        <p style={{ fontSize: 11, color: C.faint, margin: 0 }}>No internal links point to this page</p>
                      </div>
                      <span style={{ fontSize: 9, fontWeight: 800, padding: '3px 8px', background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 4, letterSpacing: '0.08em', flexShrink: 0, alignSelf: 'center' }}>ORPHAN</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Under-linked pages */}
            {underLinked.length > 0 && (
              <SectionCard
                title="Under-linked pages" count={underLinked.length}
                icon="⚡" color="#fbbf24" border="rgba(245,158,11,0.25)" bg="rgba(245,158,11,0.05)">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {underLinked.map((page, i) => {
                    const p = typeof page === 'object' ? page : { url: page };
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surfaceHi, border: '1px solid rgba(245,158,11,0.12)', borderRadius: 10, padding: '12px 16px', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#fbbf24', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.url}</p>
                          {p.inbound_count !== undefined && (
                            <p style={{ fontSize: 11, color: C.faint, margin: 0 }}>{p.inbound_count} inbound {p.inbound_count === 1 ? 'link' : 'links'}</p>
                          )}
                        </div>
                        {p.inbound_count !== undefined && (
                          <span style={{ fontSize: 18, fontWeight: 900, color: '#fbbf24', flexShrink: 0 }}>{p.inbound_count}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            {/* Link suggestions */}
            {suggestions.length > 0 && (
              <SectionCard
                title="Link suggestions" count={suggestions.length}
                icon="✦" color="#a78bfa" border="rgba(124,58,237,0.25)" bg="rgba(124,58,237,0.05)">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {suggestions.map((s, i) => {
                    const code = `<a href="${s.target_url}">${s.anchor_text || s.target_url}</a>`;
                    const sid = `sug-${i}`;
                    return (
                      <div key={i} style={{ background: C.surfaceHi, border: '1px solid rgba(124,58,237,0.15)', borderRadius: 10, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, color: C.faint }}>From:</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{s.source_url}</span>
                          <span style={{ fontSize: 11, color: C.faint }}>→</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#a78bfa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{s.target_url}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{ flex: 1, background: '#0d0d1a', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 6, padding: '7px 12px', fontSize: 11, color: '#c4b5fd', fontFamily: "'Fira Code', 'Consolas', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {code}
                          </code>
                          <button onClick={() => copyCode(code, sid)}
                            style={{ padding: '7px 14px', background: copied === sid ? 'rgba(52,211,153,0.12)' : 'rgba(124,58,237,0.1)', border: `1px solid ${copied === sid ? 'rgba(52,211,153,0.3)' : 'rgba(124,58,237,0.25)'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, color: copied === sid ? '#34d399' : '#a78bfa', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: C.font, transition: 'all 0.2s', flexShrink: 0 }}>
                            {copied === sid ? '✓ Copied' : 'Copy'}
                          </button>
                        </div>
                        {s.context && (
                          <p style={{ fontSize: 11, color: C.faint, margin: '8px 0 0', lineHeight: 1.5 }}>💡 {s.context}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            {/* All clean */}
            {orphans.length === 0 && underLinked.length === 0 && suggestions.length === 0 && (
              <div style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 16, padding: '48px 32px', textAlign: 'center' }}>
                <p style={{ fontSize: 32, margin: '0 0 12px' }}>✓</p>
                <p style={{ fontSize: 16, fontWeight: 800, color: '#34d399', margin: '0 0 6px' }}>Excellent link structure!</p>
                <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>No orphan pages, no under-linked content, no suggestions needed.</p>
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {!results && !loading && (
          <div style={{ background: C.surface, border: `1.5px dashed ${C.border}`, borderRadius: 16, padding: '56px 32px', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, background: 'rgba(59,130,246,0.1)', border: `1px solid rgba(59,130,246,0.2)`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#60a5fa', margin: '0 auto 16px' }}>⟶</div>
            <p style={{ fontSize: 16, fontWeight: 700, color: C.muted, margin: '0 0 6px' }}>Ready to analyze</p>
            <p style={{ fontSize: 13, color: C.faint, margin: 0 }}>Enter your site URL above to map all internal link relationships.</p>
          </div>
        )}
      </main>
    </div>
  );
}
